import type { VisualInspectionParameterValueDto } from '@/api/generated/HCA'
import { ConditionStatus, statusIcons, StatusTypes } from '@/configuration/status/condition-status'
import { createPblIconElement } from '@/utils/dom/createPblIconElement'
import { createElement, RefreshCcwDot } from 'lucide'
import type { CellComponent, Options } from 'tabulator-tables'
import type { ComposerTranslation } from 'vue-i18n'

export const GENERATOR_VISUAL_INSPECTION_TABULATOR_DEFAULTS = {
    layoutColumnsOnNewData: true,
    reactiveData: true,
    pagination: false,
    layout: 'fitDataTable',
    height: '100%',
    headerSort: false,
    columnHeaderVertAlign: 'bottom',
} as const satisfies Pick<
    Options,
    | 'layoutColumnsOnNewData'
    | 'reactiveData'
    | 'pagination'
    | 'layout'
    | 'height'
    | 'headerSort'
    | 'columnHeaderVertAlign'
>

export const GENERATOR_VISUAL_INSPECTION_DOM_CLASS = {
    statusCell: 'generator-visual-inspection__status-cell',
    statusIcon: 'generator-visual-inspection__status-icon',
    attachmentsCell: 'generator-visual-inspection__attachments-cell',
    attachmentsBtn: 'generator-visual-inspection__attachments-btn',
    attachmentsPblIcon: 'generator-visual-inspection__attachments-pbl-icon',
} as const

export const createGeneratorVisualInspectionStatusNumberFormatter = (t: ComposerTranslation) => {
    return (cell: CellComponent) => {
        const value: VisualInspectionParameterValueDto = cell.getValue()
        const status = value?.status ?? ConditionStatus.None
        const displayedText = t(StatusTypes[status].labelKey) ?? t('generators.conditionStatus.content.visualInspections.statusNotApplicable')

        const statusIconData = statusIcons[status]

        const cellDiv = document.createElement('div')
        cellDiv.className = 'visual-inspections-table-content-cell'

        const textSpan = document.createElement('span')
        textSpan.textContent = displayedText
        cellDiv.appendChild(textSpan)

        if (statusIconData) {
            const statusIcon = createElement(statusIconData.iconNode)
            statusIcon.classList.add('visual-inspections-table-icon')
            statusIcon.setAttribute('stroke', statusIconData.color)
            statusIcon.setAttribute('stroke-width', '3px')
            cellDiv.appendChild(statusIcon)
        }

        return cellDiv
    }
}

export const createGeneratorVisualInspectionCommentFormatter = () => {
    return (cell: CellComponent) => {
        const value = cell.getValue() as VisualInspectionParameterValueDto
        const full = value?.comment?.trim() ?? ''

        const cellDiv = document.createElement('div')
        cellDiv.className = 'generator-visual-inspection__comment-cell'

        const span = document.createElement('span')
        span.className = 'generator-visual-inspection__comment-text'
        span.textContent = full || '-'
        
        cellDiv.appendChild(span)
        return cellDiv
    }
}

export const createGeneratorVisualInspectionImagesForCommentFormatter = () => {
    return (cell: CellComponent) => {
        const value = cell.getValue() as VisualInspectionParameterValueDto
        const full = value?.imageRef?.trim() ?? ''

        const cellDiv = document.createElement('div')
        cellDiv.className = 'generator-visual-inspection__comment-cell'
        
        const span = document.createElement('span')
        span.className = 'generator-visual-inspection__comment-text'
        span.textContent = full || '-'

        cellDiv.appendChild(span)
        return cellDiv
    }
}

export const createGeneratorVisualInspectionCommentTooltip = () => {
    return (_event: UIEvent, cell: CellComponent) => {
        const value = cell.getValue() as VisualInspectionParameterValueDto
        const commentText = value?.comment?.trim()
        if (!commentText || commentText === '-') {
            return ''
        }
        const tooltipDiv = document.createElement('div')
        tooltipDiv.classList.add('generator-visual-inspection__tooltip')
        const contentDiv = document.createElement('div')
        contentDiv.classList.add('generator-visual-inspection__tooltip__content')
        contentDiv.textContent = commentText
        tooltipDiv.appendChild(contentDiv)
        return tooltipDiv
    }
}

export type GeneratorVisualInspectionAttachmentsFormatterOptions = {
    t: ComposerTranslation
    reportAnalytical: string
    cellHasVisualInspectionImage: (reportAnalytical: string, parameterId: number | undefined) => boolean
    onOpenImages: (reportAnalytical: string, parameterId: number | undefined) => void
}

export const createGeneratorVisualInspectionAttachmentsFormatter = ({
    t,
    reportAnalytical,
    cellHasVisualInspectionImage,
    onOpenImages,
}: GeneratorVisualInspectionAttachmentsFormatterOptions) => {
    return (cell: CellComponent) => {
        const value = cell.getValue() as VisualInspectionParameterValueDto
        const pid = value?.parameterId
        const cellDiv = document.createElement('div')
        cellDiv.className = GENERATOR_VISUAL_INSPECTION_DOM_CLASS.attachmentsCell
        if (!cellHasVisualInspectionImage(reportAnalytical, pid)) {
            return cellDiv
        }
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = GENERATOR_VISUAL_INSPECTION_DOM_CLASS.attachmentsBtn
        btn.setAttribute('aria-label', t('generators.conditionStatus.content.visualInspections.attachments.open'))
        btn.appendChild(
            createPblIconElement({
                name: 'image',
                className: [GENERATOR_VISUAL_INSPECTION_DOM_CLASS.attachmentsPblIcon, 'text-primary'].join(' '),
                ariaHidden: true,
            }),
        )
        btn.addEventListener('click', (e) => {
            e.stopPropagation()
            onOpenImages(reportAnalytical, pid)
        })
        cellDiv.appendChild(btn)
        return cellDiv
    }
}

export const buildGeneratorVisualInspectionReportTitleHeaderElement = (options: {
    t: ComposerTranslation,
    headerLabel: string
    analyticalNumber: string
    dateLabel: string
    onOpenReport: (analyticalNumber: string) => void
    hasImages: boolean
    onViewImages: (reportNumber: string) => void
    hasOverride: boolean
}): HTMLElement => {
    const { t, headerLabel, analyticalNumber, dateLabel, onOpenReport, hasImages, onViewImages, hasOverride } = options

    const headerCell = document.createElement('div')
    headerCell.className = 'generator-visual-inspection__header-cell'

    const linkSpan = document.createElement('span')
    linkSpan.textContent = headerLabel
    
    if (analyticalNumber) {
        linkSpan.classList.add('generator-visual-inspection__header-cell__link')
        linkSpan.setAttribute('role', 'link')
        linkSpan.tabIndex = 0
        linkSpan.addEventListener('click', () => onOpenReport(analyticalNumber))
    } 

    headerCell.appendChild(linkSpan)

    const dateSpan = document.createElement('span')
    dateSpan.textContent = ` (${dateLabel})`
    dateSpan.classList.add('generator-visual-inspection__header-cell__date')
    headerCell.appendChild(dateSpan)

    if (hasOverride) {
        const commentIcon = createElement(RefreshCcwDot)
        commentIcon.classList.add('generator-visual-inspection__header-cell__icon')
        headerCell.appendChild(commentIcon)
    }

    if (hasImages) {
        const viewImagesButton = document.createElement('span')
        viewImagesButton.textContent = t('generators.conditionStatus.content.viewImages')
        viewImagesButton.classList.add('generator-visual-inspection__header-cell__link')
        viewImagesButton.addEventListener('click', () => { onViewImages(analyticalNumber) })
        headerCell.appendChild(viewImagesButton)
    }

    return headerCell
}
